// src/components/LabelingPanel.tsx

import React, { useState, useEffect } from 'react';
import { AudioWithUrl } from '../types/audio.types';
import { ClinicalLabel, ClinicalLabelNames } from '../types/label.types';
import { useLabels } from '../hooks/useLabels';
import WaveformSingle from './WaveformSingle';

interface LabelingPanelProps {
  audio: AudioWithUrl | null;
  canLabel: boolean;
}

const PROTOCOL_VERSION = '1.0.0'; // Versão do protocolo de curadoria

const LabelingPanel: React.FC<LabelingPanelProps> = ({ audio, canLabel }) => {
  const { currentLabel, labelHistory, loading, error, saveLabel } = useLabels(audio?.audioId);
  const [selectedLabel, setSelectedLabel] = useState<ClinicalLabel | ''>('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    // Reset form when audio changes
    setSelectedLabel('');
    setComment('');
    setSuccessMessage('');
  }, [audio?.audioId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!audio || !selectedLabel) return;

    setSaving(true);
    setSuccessMessage('');

    const success = await saveLabel({
      audioId: audio.audioId,
      label: selectedLabel,
      protocol_version: PROTOCOL_VERSION,
      comment: comment.trim() || undefined
    });

    setSaving(false);

    if (success) {
      setSuccessMessage('Rótulo salvo com sucesso!');
      setSelectedLabel('');
      setComment('');

      // Limpar mensagem após 3 segundos
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (!audio) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center text-gray-500">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
            />
          </svg>
          <p className="mt-2 text-sm">Selecione um áudio para rotular</p>
        </div>
      </div>
    );
  }

  if (!canLabel) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-yellow-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-sm text-yellow-800 font-medium">
              Você não tem permissão para rotular áudios
            </p>
          </div>
          <p className="mt-2 text-xs text-yellow-700">
            Apenas usuários com perfil "Revisor Clínico" ou "Admin" podem criar rótulos.
          </p>
        </div>

        {/* Mostrar rótulo atual mesmo sem permissão */}
        {currentLabel && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Rótulo Atual</h4>
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">
                  {ClinicalLabelNames[currentLabel.label]}
                </span>
                <span className="text-xs text-gray-500">
                  {formatDate(currentLabel.created_at)}
                </span>
              </div>
              {currentLabel.comment && (
                <p className="mt-2 text-xs text-gray-600">{currentLabel.comment}</p>
              )}
              <p className="mt-2 text-xs text-gray-500">
                Por: {currentLabel.author_name}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Rotulagem Clínica</h3>
      
      {/* Waveform Visualization */}
      <div className="mb-6 border-b border-gray-200 pb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Visualização do Sinal</h4>
            <p className="text-xs text-gray-600 mt-1">
              Foco: {audio.focus} • Duração: {audio.duration_s.toFixed(1)}s
            </p>
          </div>
          <div className="text-right text-xs text-gray-500">
            <p>Audio ID: {audio.audioId}</p>
            <p className="mt-1">Qualidade: {audio.signal_quality || 'N/A'}</p>
          </div>
        </div>
        <WaveformSingle 
          audio={audio} 
          startTime={0} 
          endTime={audio.duration_s} 
        />
      </div>

      {/* Rótulo Atual */}
      {currentLabel && (
        <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-indigo-900">Rótulo Atual</span>
            {labelHistory.length > 1 && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="text-xs text-indigo-600 hover:text-indigo-800"
              >
                {showHistory ? 'Ocultar' : 'Ver'} Histórico ({labelHistory.length})
              </button>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-base font-semibold text-indigo-900">
              {ClinicalLabelNames[currentLabel.label]}
            </span>
            <span className="text-xs text-indigo-700">
              {formatDate(currentLabel.created_at)}
            </span>
          </div>
          {currentLabel.comment && (
            <p className="mt-2 text-sm text-indigo-800">{currentLabel.comment}</p>
          )}
          <p className="mt-1 text-xs text-indigo-700">Por: {currentLabel.author_name}</p>
        </div>
      )}

      {/* Histórico */}
      {showHistory && labelHistory.length > 1 && (
        <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Histórico de Alterações</h4>
          <div className="space-y-3">
            {labelHistory.slice(1).map((label, index) => (
              <div key={label.labelId} className="border-l-2 border-gray-300 pl-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">
                    {ClinicalLabelNames[label.label]}
                  </span>
                  <span className="text-xs text-gray-500">
                    v{labelHistory.length - index - 1}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  {formatDate(label.created_at)} • {label.author_name}
                </p>
                {label.comment && (
                  <p className="text-xs text-gray-600 mt-1">{label.comment}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Formulário de Rotulagem */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rótulo Clínico *
          </label>
          <select
            value={selectedLabel}
            onChange={(e) => setSelectedLabel(e.target.value as ClinicalLabel)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Selecione um rótulo...</option>
            {Object.entries(ClinicalLabelNames).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Comentários (opcional)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            placeholder="Adicione observações sobre este áudio..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
          />
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            {successMessage}
          </div>
        )}

        <button
          type="submit"
          disabled={!selectedLabel || saving || loading}
          className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {saving ? 'Salvando...' : currentLabel ? 'Atualizar Rótulo' : 'Salvar Rótulo'}
        </button>

        <p className="text-xs text-gray-500 text-center">
          Protocolo v{PROTOCOL_VERSION} • As alterações ficam registradas no histórico
        </p>
      </form>
    </div>
  );
};

export default LabelingPanel;
